import { useAbsenceTypes, useTimeline } from '@src/features/absence';
import { useAuthentication } from '@src/features/auth';
import { useFormContext } from '@src/shared/form';
import { SmartDateField } from '@src/shared/ui/fields';
import { Stack } from '@src/shared/ui/layout';
import { type ComponentPropsWithoutRef, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ScheduleFormValues } from '../Form';

type DateFieldProps = ComponentPropsWithoutRef<typeof SmartDateField>;

function FromField(props: DateFieldProps) {
  return (
    <SmartDateField
      fullWidth
      label="From"
      name="dateFrom"
      showCalendarIcon={false}
      {...props}
    />
  );
}

function ToField(props: DateFieldProps) {
  return (
    <SmartDateField
      fullWidth
      label="To"
      name="dateTo"
      showCalendarIcon={false}
      {...props}
    />
  );
}

export type DatesChangedPayload = {
  dateFrom?: Date | null;
  dateTo?: Date | null;
};

type DatesRangeFieldProps = { hours: boolean; disabled?: boolean };
export function DatesRangeField({
  hours,
  disabled = false,
}: DatesRangeFieldProps) {
  const { watch } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { user } = useAuthentication();
  const { users } = useTimeline();
  const { absenceTypes } = useAbsenceTypes();
  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');
  const userId = watch('userId');
  const typeId = watch('typeId');

  const canSelectPastDates = useMemo(() => {
    if (user?.isAdmin) return true;
    if (!userId || userId === user?.id) return false;
    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser) return false;
    const isHr = targetUser.hrId === user?.id;
    if (isHr) return true;
    const isManager = targetUser.manager?.id === user?.id;
    if (isManager) {
      if (!typeId) return false;
      const selectedType = absenceTypes.find((t) => t.id === typeId);
      return selectedType?.backdateable ?? false;
    }
    return false;
  }, [user, userId, typeId, users, absenceTypes]);

  const today = useMemo(() => new Date(), []);
  const minDateFrom = canSelectPastDates ? undefined : today;
  const minDateTo = canSelectPastDates
    ? (dateFrom ?? undefined)
    : dateFrom && dateFrom > today
      ? dateFrom
      : today;

  return (
    <Stack spacing={1} direction="row" sx={{ mb: 2.5 }}>
      <FromField
        disabled={disabled}
        label={hours ? 'Date' : 'From'}
        minDate={minDateFrom}
        maxDate={dateTo ?? undefined}
        required
      />
      {!hours && <ToField disabled={disabled} minDate={minDateTo} required />}
    </Stack>
  );
}
